import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bell,
  CheckCircle2,
  Edit2,
  Mail,
  MessageSquareText,
  Plus,
  Radio,
  RefreshCw,
  Save,
  Send,
  Trash2,
  X,
  Archive,
} from 'lucide-react';
import useCompanies from '@/hooks/useCompanies';
import {
  chatMonitoringService,
  ChatMentionEvent,
  ChatMonitoredTender,
  ChatMonitorSource,
} from '@/services/chatMonitoringService';

const sourceTypes = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'WEBHOOK', label: 'Webhook' },
  { value: 'API', label: 'API' },
  { value: 'BBMNET_API', label: 'API BBMNET' },
  { value: 'BROWSER', label: 'Navegador' },
  { value: 'EMAIL', label: 'E-mail' },
];

const portalPresets = [
  { portalName: 'Compras.gov.br - Sala de disputa', portalUrl: 'https://www.gov.br/compras/pt-br', sourceType: 'WEBHOOK' },
  { portalName: 'Portal de Compras Públicas - Chat', portalUrl: 'https://www.portaldecompraspublicas.com.br', sourceType: 'BROWSER' },
  { portalName: 'Compras BR - Chat', portalUrl: 'https://comprasbr.com.br', sourceType: 'BROWSER' },
  { portalName: 'BNC - Bolsa Nacional de Compras', portalUrl: 'https://bnc.org.br', sourceType: 'BROWSER' },
  { portalName: 'BLL Compras - Chat', portalUrl: 'https://bllcompras.com', sourceType: 'BROWSER' },
  { portalName: 'BBMNET Licitações - Chat e Sala', portalUrl: 'https://novobbmnet.com.br', sourceType: 'BBMNET_API' },
  { portalName: 'Licitanet - Chat do pregão', portalUrl: 'https://licitanet.com.br', sourceType: 'MANUAL' },
] as const;

const emptySourceForm = {
  portalName: '',
  portalUrl: '',
  sourceType: 'MANUAL',
  companyName: '',
  companyId: '',
  mentionTerms: '',
  enabled: true,
  notifyEmail: true,
  notifyWhatsapp: true,
};

const emptyTenderForm = {
  id: '',
  sourceId: '',
  tenderRef: '',
  externalLotId: '',
  organ: '',
  object: '',
  sessionUrl: '',
  status: 'ACTIVE',
  enabled: true,
  notes: '',
};

const statusClass: Record<string, string> = {
  NOTIFIED: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900',
  PARTIAL: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900',
  ERROR: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900',
  SKIPPED: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/50 dark:text-slate-300 dark:border-slate-800',
  PENDING: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900',
  VERIFIED: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/50 dark:text-slate-300 dark:border-slate-800',
};

function parseTerms(value: string): string[] {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed.map(String);
  } catch {}
  return value.split(/[\n,;]+/g).map((item) => item.trim()).filter(Boolean);
}

function formatTerms(value: string): string {
  return parseTerms(value).join('\n');
}

function formatDate(value?: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function playDirectMentionTone() {
  const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) return;
  try {
    const context = new AudioContextConstructor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.5);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.52);
    oscillator.addEventListener('ended', () => void context.close());
  } catch {
    // Alguns navegadores exigem interação prévia do usuário para reprodução.
  }
}

const ChatMonitoring: React.FC = () => {
  const { companies } = useCompanies();
  const [sources, setSources] = useState<ChatMonitorSource[]>([]);
  const [monitoredTenders, setMonitoredTenders] = useState<ChatMonitoredTender[]>([]);
  const [events, setEvents] = useState<ChatMentionEvent[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [sourceForm, setSourceForm] = useState({ ...emptySourceForm });
  const [tenderForm, setTenderForm] = useState({ ...emptyTenderForm });
  const [testForm, setTestForm] = useState({
    sourceId: '',
    tenderRef: 'PE 90012/2026',
    senderName: 'Pregoeiro',
    messageText: 'Solicitamos que a empresa Soltech confirme ciência no chat da sessão.',
  });
  const [notice, setNotice] = useState<string | null>(null);
  const initializedEventIds = useRef(false);
  const knownEventIds = useRef(new Set<string>());

  const metrics = useMemo(() => {
    const total = events.length;
    const notified = events.filter((event) => event.status === 'NOTIFIED' || event.status === 'PARTIAL').length;
    const pending = events.filter((event) => event.status === 'PENDING').length;
    const monitored = monitoredTenders.filter((tender) => tender.enabled && tender.status === 'ACTIVE').length;
    return { total, notified, pending, active: sources.filter((source) => source.enabled).length, monitored };
  }, [events, sources, monitoredTenders]);

  const groupedEvents = useMemo(() => {
    const groups = new Map<string, ChatMentionEvent[]>();
    for (const event of events) {
      const key = event.tenderRef?.trim() || 'Sem pregão informado';
      groups.set(key, [...(groups.get(key) || []), event]);
    }
    return Array.from(groups.entries()).map(([tenderRef, items]) => ({
      tenderRef,
      items,
      lastOccurrence: items[0]?.occurredAt,
      portals: Array.from(new Set(items.map((item) => item.portalName))),
    }));
  }, [events]);

  useEffect(() => {
    const newDirectMentions = events.filter((event) => event.directMention && !knownEventIds.current.has(event.id));
    if (initializedEventIds.current && newDirectMentions.length > 0) playDirectMentionTone();
    knownEventIds.current = new Set(events.map((event) => event.id));
    initializedEventIds.current = true;
  }, [events]);

  const loadData = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const [sourceList, tenderList, eventList] = await Promise.all([
        chatMonitoringService.listSources(),
        chatMonitoringService.listMonitoredTenders(),
        chatMonitoringService.listEvents(100, showArchived),
      ]);
      setSources(sourceList);
      setMonitoredTenders(tenderList);
      setEvents(eventList);
      if (!testForm.sourceId && sourceList[0]?.id) {
        setTestForm((current) => ({ ...current, sourceId: sourceList[0].id }));
      }
      if (!tenderForm.sourceId && sourceList[0]?.id) {
        setTenderForm((current) => ({ ...current, sourceId: sourceList[0].id }));
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao carregar monitoramento');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
    const timer = window.setInterval(() => void loadData(false), 15_000);
    return () => window.clearInterval(timer);
  }, [showArchived]);

  const resetSourceForm = () => {
    setEditingId(null);
    setSourceForm({ ...emptySourceForm });
  };

  const resetTenderForm = () => {
    setTenderForm({ ...emptyTenderForm, sourceId: sources[0]?.id || '' });
  };

  const applyPortalPreset = (portalName: string) => {
    const preset = portalPresets.find((item) => item.portalName === portalName);
    if (!preset) return;
    setSourceForm((current) => ({
      ...current,
      portalName: preset.portalName,
      portalUrl: preset.portalUrl,
      sourceType: preset.sourceType,
    }));
  };

  const editSource = (source: ChatMonitorSource) => {
    setEditingId(source.id);
    setSourceForm({
      portalName: source.portalName,
      portalUrl: source.portalUrl || '',
      sourceType: source.sourceType,
      companyName: source.companyName,
      companyId: source.companyId || '',
      mentionTerms: formatTerms(source.mentionTerms),
      enabled: source.enabled,
      notifyEmail: source.notifyEmail,
      notifyWhatsapp: source.notifyWhatsapp,
    });
  };

  const saveSource = async () => {
    if (!sourceForm.portalName.trim() || !sourceForm.companyName.trim()) {
      setNotice('Preencha portal e empresa monitorada.');
      return;
    }

    setSaving(true);
    setNotice(null);
    try {
      const payload = {
        ...sourceForm,
        mentionTerms: parseTerms(sourceForm.mentionTerms || sourceForm.companyName),
      };
      if (editingId) {
        await chatMonitoringService.updateSource(editingId, payload);
      } else {
        await chatMonitoringService.createSource(payload);
      }
      resetSourceForm();
      await loadData();
      setNotice('Fonte salva.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao salvar fonte');
    } finally {
      setSaving(false);
    }
  };

  const deleteSource = async (source: ChatMonitorSource) => {
    if (!window.confirm(`Excluir ${source.portalName}?`)) return;
    try {
      await chatMonitoringService.deleteSource(source.id);
      await loadData();
      setNotice('Fonte excluída.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao excluir fonte');
    }
  };

  const editMonitoredTender = (tender: ChatMonitoredTender) => {
    setTenderForm({
      id: tender.id,
      sourceId: tender.sourceId,
      tenderRef: tender.tenderRef,
      externalLotId: tender.externalLotId || '',
      organ: tender.organ || '',
      object: tender.object || '',
      sessionUrl: tender.sessionUrl || '',
      status: tender.status || 'ACTIVE',
      enabled: tender.enabled,
      notes: tender.notes || '',
    });
  };

  const saveMonitoredTender = async () => {
    if (!tenderForm.sourceId || !tenderForm.tenderRef.trim()) {
      setNotice('Selecione o portal e informe o pregão/licitação.');
      return;
    }
    const source = sources.find((item) => item.id === tenderForm.sourceId);
    if (source?.sourceType === 'BBMNET_API' && !tenderForm.externalLotId.trim()) {
      setNotice('Informe o ID do lote BBMNET para ativar a leitura automática de chat e sala.');
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      await chatMonitoringService.saveMonitoredTender(tenderForm);
      resetTenderForm();
      await loadData();
      setNotice('Licitação monitorada salva.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao salvar licitação monitorada');
    } finally {
      setSaving(false);
    }
  };

  const deleteMonitoredTender = async (tender: ChatMonitoredTender) => {
    if (!window.confirm(`Remover monitoramento de ${tender.tenderRef}?`)) return;
    try {
      await chatMonitoringService.deleteMonitoredTender(tender.id);
      await loadData();
      setNotice('Licitação removida do monitoramento.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao remover licitação monitorada');
    }
  };

  const runTest = async () => {
    if (!testForm.messageText.trim()) {
      setNotice('Informe a mensagem do chat.');
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const selected = sources.find((source) => source.id === testForm.sourceId);
      const result = await chatMonitoringService.ingest({
        ...testForm,
        portalName: selected?.portalName,
      });
      await loadData();
      window.dispatchEvent(new Event('chat-monitoring:updated'));
      setNotice(result.matched
        ? `Alerta detectado e distribuído em ${result.events.length} fonte(s).`
        : result.duplicatesIgnored > 0
          ? 'Mensagem já recebida anteriormente; a notificação duplicada foi ignorada.'
          : `Sem menção nas ${result.sourcesChecked} fonte(s) verificadas.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao testar mensagem');
    } finally {
      setSaving(false);
    }
  };

  const retryNotification = async (eventId: string) => {
    setSaving(true);
    try {
      await chatMonitoringService.retryNotification(eventId);
      await loadData();
      setNotice('Notificação reenviada.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao reenviar notificação');
    } finally {
      setSaving(false);
    }
  };

  const getCurrentUserName = () => {
    try {
      const raw = localStorage.getItem('user');
      const user = raw ? JSON.parse(raw) : null;
      return user?.name || user?.email || 'Operador';
    } catch {
      return 'Operador';
    }
  };

  const verifyEvent = async (eventId: string) => {
    setSaving(true);
    try {
      await chatMonitoringService.verifyEvent(eventId, { verifiedBy: getCurrentUserName() });
      await loadData();
      window.dispatchEvent(new Event('chat-monitoring:updated'));
      setNotice('Mensagem verificada e arquivada.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao arquivar mensagem');
    } finally {
      setSaving(false);
    }
  };

  const verifyTender = async (tenderRef: string) => {
    if (tenderRef === 'Sem pregão informado') {
      setNotice('Arquive individualmente mensagens sem pregão informado.');
      return;
    }
    setSaving(true);
    try {
      const result = await chatMonitoringService.verifyTender(tenderRef, { verifiedBy: getCurrentUserName() });
      await loadData();
      window.dispatchEvent(new Event('chat-monitoring:updated'));
      setNotice(`${result.count} mensagem(ns) do pregão ${tenderRef} verificadas e arquivadas.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Erro ao arquivar pregão');
    } finally {
      setSaving(false);
    }
  };

  const selectCompany = (companyId: string) => {
    const company = companies.find((item) => item.id === companyId);
    setSourceForm((current) => ({
      ...current,
      companyId,
      companyName: company?.tradeName || company?.corporateName || current.companyName,
      mentionTerms: current.mentionTerms || [company?.tradeName, company?.corporateName, company?.cnpj].filter(Boolean).join('\n'),
    }));
  };

  return (
    <div className="p-4 lg:p-6 space-y-4">
      {notice && (
        <div className="flex items-center justify-between rounded-md border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-3 py-2 text-sm text-ink-text dark:text-ink-text-dark">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} className="p-1 rounded hover:bg-surface-alt dark:hover:bg-surface-dark-alt">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
        <MetricCard icon={Radio} label="Fontes ativas" value={metrics.active} />
        <MetricCard icon={CheckCircle2} label="Pregões monitorados" value={metrics.monitored} />
        <MetricCard icon={MessageSquareText} label="Mensagens" value={metrics.total} />
        <MetricCard icon={Bell} label="Notificadas" value={metrics.notified} />
        <MetricCard icon={RefreshCw} label="Pendentes" value={metrics.pending} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,420px)_1fr] gap-4">
        <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
          <div className="flex items-center justify-between border-b border-line dark:border-line-dark px-4 py-3">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-brand-600" />
              <h2 className="text-sm font-semibold">Fonte monitorada</h2>
            </div>
            {editingId && (
              <button type="button" onClick={resetSourceForm} className="text-xs text-ink-subtitle hover:text-ink-title dark:text-ink-subtitle-dark dark:hover:text-ink-title-dark">
                Novo
              </button>
            )}
          </div>
            <div className="p-4 space-y-3">
            <Field label="Modelo de portal">
              <select className="input" value="" onChange={(e) => applyPortalPreset(e.target.value)}>
                <option value="">Selecionar portal...</option>
                {portalPresets.map((preset) => (
                  <option key={preset.portalName} value={preset.portalName}>{preset.portalName}</option>
                ))}
              </select>
            </Field>
            <Field label="Portal">
              <input className="input" value={sourceForm.portalName} onChange={(e) => setSourceForm({ ...sourceForm, portalName: e.target.value })} />
            </Field>
            <Field label="URL">
              <input className="input" value={sourceForm.portalUrl} onChange={(e) => setSourceForm({ ...sourceForm, portalUrl: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tipo">
                <select className="input" value={sourceForm.sourceType} onChange={(e) => setSourceForm({ ...sourceForm, sourceType: e.target.value })}>
                  {sourceTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </Field>
              <Field label="Empresa">
                <select className="input" value={sourceForm.companyId} onChange={(e) => selectCompany(e.target.value)}>
                  <option value="">Manual</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>{company.tradeName || company.corporateName}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Nome monitorado">
              <input className="input" value={sourceForm.companyName} onChange={(e) => setSourceForm({ ...sourceForm, companyName: e.target.value })} />
            </Field>
            <Field label="Termos">
              <textarea className="input min-h-[104px] resize-y" value={sourceForm.mentionTerms} onChange={(e) => setSourceForm({ ...sourceForm, mentionTerms: e.target.value })} />
            </Field>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Toggle label="Ativa" checked={sourceForm.enabled} onChange={(enabled) => setSourceForm({ ...sourceForm, enabled })} />
              <Toggle label="Email" checked={sourceForm.notifyEmail} onChange={(notifyEmail) => setSourceForm({ ...sourceForm, notifyEmail })} />
              <Toggle label="WhatsApp" checked={sourceForm.notifyWhatsapp} onChange={(notifyWhatsapp) => setSourceForm({ ...sourceForm, notifyWhatsapp })} />
            </div>
            <button type="button" onClick={saveSource} disabled={saving} className="btn-primary w-full">
              <Save className="w-4 h-4" />
              {editingId ? 'Salvar alterações' : 'Adicionar fonte'}
            </button>
          </div>
        </section>

        <div className="space-y-4">
          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
            <div className="flex items-center justify-between border-b border-line dark:border-line-dark px-4 py-3">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-brand-600" />
                <h2 className="text-sm font-semibold">Teste de monitoramento</h2>
              </div>
              <button type="button" onClick={loadData} className="icon-btn" title="Atualizar">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-3 p-4">
              <div className="space-y-3">
                <Field label="Fonte">
                  <select className="input" value={testForm.sourceId} onChange={(e) => setTestForm({ ...testForm, sourceId: e.target.value })}>
                    {sources.map((source) => <option key={source.id} value={source.id}>{source.portalName}</option>)}
                  </select>
                </Field>
                <Field label="Licitação">
                  <input className="input" value={testForm.tenderRef} onChange={(e) => setTestForm({ ...testForm, tenderRef: e.target.value })} />
                </Field>
                <Field label="Remetente">
                  <input className="input" value={testForm.senderName} onChange={(e) => setTestForm({ ...testForm, senderName: e.target.value })} />
                </Field>
              </div>
              <div className="space-y-3">
                <Field label="Mensagem">
                  <textarea className="input min-h-[132px] resize-y" value={testForm.messageText} onChange={(e) => setTestForm({ ...testForm, messageText: e.target.value })} />
                </Field>
                <button type="button" onClick={runTest} disabled={saving || sources.length === 0} className="btn-primary">
                  <Send className="w-4 h-4" />
                  Processar
                </button>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-line dark:border-line-dark px-4 py-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-brand-600" />
                <h2 className="text-sm font-semibold">Licitações monitoradas por portal</h2>
              </div>
              {tenderForm.id && (
                <button type="button" onClick={resetTenderForm} className="text-xs text-ink-subtitle hover:text-ink-title dark:text-ink-subtitle-dark dark:hover:text-ink-title-dark">
                  Novo cadastro
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(260px,360px)_1fr] gap-4 p-4">
              <div className="space-y-3">
                <Field label="Portal / fonte">
                  <select className="input" value={tenderForm.sourceId} onChange={(e) => setTenderForm({ ...tenderForm, sourceId: e.target.value })}>
                    <option value="">Selecionar portal...</option>
                    {sources.map((source) => (
                      <option key={source.id} value={source.id}>{source.portalName}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Pregão / licitação">
                  <input className="input" placeholder="Ex: PE 90012/2026" value={tenderForm.tenderRef} onChange={(e) => setTenderForm({ ...tenderForm, tenderRef: e.target.value })} />
                </Field>
                <Field label="ID do lote BBMNET">
                  <input className="input" placeholder="Necessário para leitura automática do chat" value={tenderForm.externalLotId} onChange={(e) => setTenderForm({ ...tenderForm, externalLotId: e.target.value })} />
                </Field>
                <Field label="Órgão">
                  <input className="input" value={tenderForm.organ} onChange={(e) => setTenderForm({ ...tenderForm, organ: e.target.value })} />
                </Field>
                <Field label="URL da sala / disputa">
                  <input className="input" value={tenderForm.sessionUrl} onChange={(e) => setTenderForm({ ...tenderForm, sessionUrl: e.target.value })} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Status">
                    <select className="input" value={tenderForm.status} onChange={(e) => setTenderForm({ ...tenderForm, status: e.target.value })}>
                      <option value="ACTIVE">Ativa</option>
                      <option value="PAUSED">Pausada</option>
                      <option value="FINISHED">Finalizada</option>
                    </select>
                  </Field>
                  <Toggle label="Monitorar" checked={tenderForm.enabled} onChange={(enabled) => setTenderForm({ ...tenderForm, enabled })} />
                </div>
                <Field label="Objeto / observações">
                  <textarea className="input min-h-[88px] resize-y" value={tenderForm.object} onChange={(e) => setTenderForm({ ...tenderForm, object: e.target.value })} />
                </Field>
                <button type="button" onClick={saveMonitoredTender} disabled={saving || sources.length === 0} className="btn-primary w-full">
                  <Save className="w-4 h-4" />
                  {tenderForm.id ? 'Salvar monitoramento' : 'Cadastrar monitoramento'}
                </button>
              </div>

              <div className="rounded-md border border-line dark:border-line-dark overflow-hidden">
                <div className="divide-y divide-line dark:divide-line-dark">
                  {monitoredTenders.map((tender) => (
                    <div key={tender.id} className="p-3 flex flex-col md:flex-row md:items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-sm">{tender.tenderRef}</span>
                          <span className={`px-2 py-0.5 rounded-full border text-[11px] ${tender.enabled && tender.status === 'ACTIVE' ? statusClass.NOTIFIED : statusClass.SKIPPED}`}>
                            {tender.enabled ? tender.status : 'INATIVA'}
                          </span>
                        </div>
                        <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
                          {tender.source?.portalName || 'Portal'}{tender.organ ? ` · ${tender.organ}` : ''}
                          {tender.roomStatus ? ` · ${tender.roomStatus}` : ''}
                        </div>
                        {tender.object ? <p className="mt-2 text-xs text-ink-text dark:text-ink-text-dark line-clamp-2">{tender.object}</p> : null}
                        {tender.sessionUrl ? (
                          <a className="mt-1 inline-block text-xs text-brand-700 dark:text-brand-300 hover:underline" href={tender.sessionUrl} target="_blank" rel="noreferrer">
                            Abrir sala
                          </a>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => editMonitoredTender(tender)} className="icon-btn" title="Editar monitoramento">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => deleteMonitoredTender(tender)} className="icon-btn text-rose-600" title="Remover monitoramento">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {!loading && monitoredTenders.length === 0 && <EmptyState text="Nenhuma licitação cadastrada para monitoramento." />}
                  {loading && <EmptyState text="Carregando licitações monitoradas..." />}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
            <div className="flex items-center gap-2 border-b border-line dark:border-line-dark px-4 py-3">
              <MessageSquareText className="w-4 h-4 text-brand-600" />
                <h2 className="text-sm font-semibold">Fontes de monitoramento</h2>
            </div>
            <div className="divide-y divide-line dark:divide-line-dark">
              {loading ? <EmptyState text="Carregando..." /> : sources.map((source) => (
                <div key={source.id} className="flex flex-col md:flex-row md:items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm truncate">{source.portalName}</span>
                      <span className={`px-2 py-0.5 text-[11px] rounded-full border ${source.enabled ? statusClass.NOTIFIED : statusClass.SKIPPED}`}>
                        {source.enabled ? 'Ativa' : 'Inativa'}
                      </span>
                    </div>
                    <div className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark mt-1">
                      {source.companyName} · {source.sourceType} · {source._count?.events || 0} evento(s)
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => editSource(source)} className="icon-btn" title="Editar">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => deleteSource(source)} className="icon-btn text-rose-600" title="Excluir">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {!loading && sources.length === 0 && <EmptyState text="Nenhuma fonte cadastrada." />}
            </div>
          </section>

          <section className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-line dark:border-line-dark px-4 py-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-brand-600" />
                <h2 className="text-sm font-semibold">{showArchived ? 'Atualizações arquivadas' : 'Atualizações por pregão'}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowArchived(false)}
                  className={`px-3 py-1.5 rounded-md text-xs border ${!showArchived ? 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-950/40 dark:text-brand-300 dark:border-brand-900' : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'}`}
                >
                  Ativas
                </button>
                <button
                  type="button"
                  onClick={() => setShowArchived(true)}
                  className={`px-3 py-1.5 rounded-md text-xs border ${showArchived ? 'bg-brand-50 text-brand-700 border-brand-200 dark:bg-brand-950/40 dark:text-brand-300 dark:border-brand-900' : 'border-line dark:border-line-dark text-ink-subtitle dark:text-ink-subtitle-dark'}`}
                >
                  Arquivadas
                </button>
              </div>
            </div>
            <div className="divide-y divide-line dark:divide-line-dark">
              {groupedEvents.map((group) => (
                <div key={group.tenderRef} className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-sm text-ink-title dark:text-ink-title-dark">{group.tenderRef}</h3>
                        <span className="px-2 py-0.5 rounded-full border border-line dark:border-line-dark text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">
                          {group.items.length} mensagem(ns)
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-ink-subtitle dark:text-ink-subtitle-dark">
                        {group.portals.join(', ')} · última em {formatDate(group.lastOccurrence)}
                      </div>
                    </div>
                    {!showArchived && (
                      <button
                        type="button"
                        onClick={() => verifyTender(group.tenderRef)}
                        disabled={saving}
                        className="btn-primary min-w-[180px]"
                      >
                        <Archive className="w-4 h-4" />
                        Verificar pregão
                      </button>
                    )}
                  </div>

                  <div className="mt-3 space-y-2">
                    {group.items.map((event) => (
                      <div key={event.id} className="rounded-md border border-line dark:border-line-dark bg-surface-alt dark:bg-surface-dark-alt p-3">
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-medium text-ink-title dark:text-ink-title-dark">{event.portalName}</span>
                              <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] ${statusClass[event.status] || statusClass.PENDING}`}>{event.status}</span>
                              {event.directMention && <span className="inline-flex px-2 py-0.5 rounded-full border text-[11px] bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900">Citação direta · som</span>}
                              <span className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">{formatDate(event.occurredAt)}</span>
                            </div>
                            <p className="mt-2 text-sm text-ink-text dark:text-ink-text-dark">{event.messageText}</p>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {parseTerms(event.matchedTerms).map((term) => (
                                <span key={term} className="px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 text-[11px] dark:bg-brand-950/40 dark:text-brand-300">{term}</span>
                              ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark">
                              <Mail className="w-3 h-3" /> {event.emailStatus || '-'}
                              <Bell className="w-3 h-3 ml-1" /> {event.pushStatus || '-'}
                              <MessageSquareText className="w-3 h-3 ml-1" /> {event.whatsappStatus || '-'}
                              {event.verifiedAt ? <span>Verificado por {event.verifiedBy || 'operador'} em {formatDate(event.verifiedAt)}</span> : null}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 md:justify-end">
                            {!showArchived && (
                              <button type="button" onClick={() => verifyEvent(event.id)} className="icon-btn text-emerald-600" title="Marcar como verificada">
                                <Archive className="w-4 h-4" />
                              </button>
                            )}
                            <button type="button" onClick={() => retryNotification(event.id)} className="icon-btn" title="Reenviar">
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {groupedEvents.length === 0 && (
                <EmptyState text={showArchived ? 'Nenhuma menção arquivada.' : 'Nenhuma menção ativa.'} />
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

function MetricCard({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-line dark:border-line-dark bg-surface dark:bg-surface-dark px-4 py-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-subtitle dark:text-ink-subtitle-dark">{label}</span>
        <Icon className="w-4 h-4 text-brand-600" />
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs font-medium text-ink-subtitle dark:text-ink-subtitle-dark space-y-1">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 rounded-md border border-line dark:border-line-dark px-2 py-2 text-ink-text dark:text-ink-text-dark">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="accent-brand-600" />
      <span>{checked ? <CheckCircle2 className="inline w-3.5 h-3.5 mr-1 text-emerald-600" /> : null}{label}</span>
    </label>
  );
}

function EmptyState({ text }: { text: string }) {
  return <div className="px-4 py-8 text-center text-sm text-ink-subtitle dark:text-ink-subtitle-dark">{text}</div>;
}

export default ChatMonitoring;
